/**
 * Política de Privacidad, en español.
 *
 * Escrita a partir del esquema real de la base de datos y de las llamadas que
 * hace el servidor, no de una plantilla. Si cambia lo que se recoge o a quién
 * se le manda, este archivo cambia con ello — y sube la fecha.
 */
export default {
    titulo: 'Política de Privacidad',
    actualizado: '10 de septiembre de 2026',

    intro: [
        'ShippingFULL es una herramienta de dropshipping operada desde Nueva Jersey, Estados Unidos. Esta política explica qué datos recogemos, por qué, con quién los compartimos y qué puedes hacer al respecto.',
        'Hay algo que conviene entender desde el principio, porque condiciona todo lo demás: esta aplicación maneja dos clases de personas muy distintas. Están **nuestros usuarios** —tú, que abres una cuenta— y están **los compradores de tus usuarios**, que nunca han oído hablar de nosotros y cuyos datos llegan aquí porque tú conectas tu tienda.'
    ],

    secciones: [
        {
            titulo: '1. Qué datos recogemos de ti, como usuario',
            parrafos: [
                'Al crear una cuenta guardamos tu **correo electrónico**, tu **nombre** y una **huella criptográfica de tu contraseña** — nunca la contraseña en sí, de modo que ni nosotros podemos leerla.',
                'Si entras con Google, guardamos además el identificador que Google nos da y la dirección de tu foto de perfil. No recibimos tu contraseña de Google.',
                'Mientras usas la aplicación se registran la fecha de tu último acceso, tu nivel de suscripción y un identificador de cliente de Stripe si contratas un plan de pago.',
                'Al pedir restablecer la contraseña se guarda un testigo temporal que **caduca a los 30 minutos** y se borra en cuanto se usa.'
            ]
        },
        {
            titulo: '2. Qué datos de terceros pasan por aquí',
            parrafos: [
                'Cuando conectas una tienda y sincronizas tus ventas, recibimos de esa plataforma los datos de tus compradores: **nombre, correo electrónico, teléfono, dirección de envío y dirección de facturación**, junto con lo que compraron y por cuánto.',
                'Esas personas no son usuarias nuestras y no han aceptado esta política. Nosotros actuamos como **encargados del tratamiento por cuenta tuya**: los guardamos para que puedas gestionar y cumplir tus pedidos, y no los usamos para nada más. **Eres tú quien responde ante tus compradores** de cómo se tratan sus datos.',
                'No vendemos esos datos, no los cedemos a anunciantes, no los usamos para perfilar a nadie y no los mandamos a ningún sistema de inteligencia artificial.'
            ]
        },
        {
            titulo: '3. Las credenciales de tus tiendas',
            parrafos: [
                'Al conectar una tienda guardamos las credenciales que nos autoriza esa plataforma —claves y testigos de acceso— **cifradas**, con una clave que vive solo en la configuración del servidor y nunca en el código.',
                'Con ellas hacemos únicamente lo que tú pides: publicar un producto, consultar el estado de un anuncio, retirarlo o leer tus ventas. Puedes desconectar una tienda cuando quieras y sus credenciales dejan de servir.'
            ]
        },
        {
            titulo: '4. Con quién compartimos datos, y para qué',
            parrafos: [
                'No vendemos datos personales. Los compartimos solo con los proveedores que hacen falta para que la aplicación funcione:',
                '**Supabase** (Estados Unidos) aloja la base de datos. **Render** ejecuta el servidor. **Vercel** sirve la interfaz.',
                '**Stripe** procesa los pagos de las suscripciones. No recibimos ni guardamos los números de tu tarjeta: van directos a Stripe.',
                '**Resend** entrega los correos que la aplicación envía, que hoy son solo los de restablecer la contraseña.',
                '**eBay, CJ Dropshipping, Amazon y AliExpress** reciben las consultas de búsqueda y, cuando publicas, los datos del producto. Al cumplir un pedido, el proveedor recibe la dirección de envío del comprador — sin eso no hay forma de entregar el paquete.',
                '**Google (Gemini)** y **Anthropic (Claude)** analizan productos y traducen términos de búsqueda. Lo hemos comprobado línea por línea: **a estos servicios solo se les manda información de producto** —título, descripción, precio, valoración, categoría— **y el término que escribes en el buscador. Ningún dato personal, ni tuyo ni de tus compradores.**'
            ]
        },
        {
            titulo: '5. Cuánto tiempo lo guardamos',
            parrafos: [
                'Los datos de tu cuenta, mientras la cuenta exista. Los pedidos y sus datos de comprador, mientras mantengas la tienda conectada o hasta que los borres.',
                'Los testigos de recuperación de contraseña caducan a los 30 minutos. Las sesiones caducan a los siete días. Los resultados de búsqueda se guardan en memoria una hora para no castigar a los proveedores.',
                'Si cierras tu cuenta, escríbenos y borramos tus datos. Ten en cuenta que los datos que ya estén en eBay, en Stripe o en tu proveedor se rigen por las políticas de cada uno, no por la nuestra.'
            ]
        },
        {
            titulo: '6. Tus derechos',
            parrafos: [
                'Puedes pedirnos acceso a los datos que tenemos sobre ti, su corrección, su borrado o una copia en formato legible. Escribe a **support@shippingfull.com** y responderemos en un plazo razonable.',
                'Si resides en **Nueva Jersey**, **California** u otro estado con ley de privacidad propia, tienes además los derechos que esa ley te reconozca, incluido el de no ser objeto de venta de tus datos — algo que en cualquier caso no hacemos.',
                'Si resides en la **Unión Europea o el Reino Unido**: esta aplicación se opera desde Estados Unidos y no está diseñada específicamente para cumplir el Reglamento General de Protección de Datos. Si te aplica esa normativa, tenlo en cuenta antes de usarla o de meter en ella datos de compradores europeos.'
            ]
        },
        {
            titulo: '7. Seguridad, dicha con honestidad',
            parrafos: [
                'Las contraseñas se guardan como huella criptográfica. Las credenciales de tienda van cifradas. El tráfico viaja por conexión segura. Los datos de compradores no se escriben en los registros del servidor.',
                'Dicho eso: ningún sistema es invulnerable, y prometer lo contrario sería mentir. Si alguna vez hubiera una brecha que afecte a tus datos, te lo diremos.'
            ]
        },
        {
            titulo: '8. Menores',
            parrafos: [
                'ShippingFULL es una herramienta de negocio y no está dirigida a menores de 18 años. No recogemos datos de menores a sabiendas.'
            ]
        },
        {
            titulo: '9. Cambios',
            parrafos: [
                'Si cambiamos esta política, actualizaremos la fecha del encabezado. Si el cambio es importante, te avisaremos dentro de la aplicación.'
            ]
        },
        {
            titulo: '10. Contacto',
            parrafos: [
                'Para cualquier cuestión sobre privacidad: **support@shippingfull.com**.'
            ]
        }
    ]
};
